import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Copy, Check, BookOpen, ShieldQuestion, ChevronDown, ArrowUpRight } from "lucide-react";

const CONFIDENCE = {
  green: { dot: "🟢", label: "Consensus historique fort", cls: "text-emerald-400 border-emerald-500/30 bg-emerald-500/5" },
  yellow: { dot: "🟡", label: "Sujet débattu entre chercheurs", cls: "text-amber-400 border-amber-500/30 bg-amber-500/5" },
  red: { dot: "🔴", label: "Hypothèse ou théorie minoritaire", cls: "text-red-400 border-red-500/30 bg-red-500/5" },
};
const DOT = { green: "🟢", yellow: "🟡", red: "🔴" };

function CopyBtn({ text, testid }) {
  const [done, setDone] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text || "");
    setDone(true);
    toast.success("Copié !");
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <button data-testid={testid} onClick={copy} className="text-white/40 hover:text-gold transition-colors">
      {done ? <Check size={16} /> : <Copy size={16} />}
    </button>
  );
}

export default function ReplyResult({ data, idPrefix = "reply" }) {
  const replies = data?.replies || [];
  const [tab, setTab] = useState(0);
  const [showOpp, setShowOpp] = useState(false);
  const conf = data?.confidence?.level ? CONFIDENCE[data.confidence.level] : null;
  const sources = data?.sources || {};
  const principales = sources.principales || [];
  const livres = sources.livres || [];
  const auteurs = sources.auteurs || [];
  const hasSources = principales.length > 0 || livres.length > 0 || auteurs.length > 0;
  const opp = data?.opposing;
  const active = replies[tab] || replies[0];

  return (
    <div className="space-y-4" data-testid={`${idPrefix}-result`}>
      {/* Réponse courte */}
      {data?.short && (
        <div className="card-surface p-5" data-testid={`${idPrefix}-short`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-gold text-xs uppercase tracking-[0.18em]">Réponse courte</span>
            <CopyBtn text={data.short} testid={`${idPrefix}-short-copy`} />
          </div>
          <p className="text-white/90 text-sm md:text-base leading-relaxed">{data.short}</p>
        </div>
      )}

      {/* Réponse développée */}
      {data?.developed && (
        <div className="card-surface p-5" data-testid={`${idPrefix}-developed`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-gold text-xs uppercase tracking-[0.18em]">Réponse développée</span>
            <CopyBtn text={data.developed} testid={`${idPrefix}-developed-copy`} />
          </div>
          <p className="text-white/75 text-sm leading-relaxed whitespace-pre-line">{data.developed}</p>
        </div>
      )}

      {/* Onglets de styles */}
      {replies.length > 0 && (
        <div className="card-surface p-5" data-testid={`${idPrefix}-styles`}>
          <span className="text-gold text-xs uppercase tracking-[0.18em] block mb-3">Styles de réponse</span>
          <div className="flex flex-wrap gap-2 mb-4">
            {replies.map((r, i) => (
              <button key={i} data-testid={`${idPrefix}-style-tab-${i}`} onClick={() => setTab(i)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                  i === tab ? "bg-gold/15 border-gold/50 text-gold" : "border-white/10 text-white/50 hover:text-white hover:border-white/25"
                }`}>
                {r.style}
              </button>
            ))}
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={tab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }} className="relative">
              <div className="flex items-start justify-between gap-3">
                <p className="text-white/85 text-sm leading-relaxed flex-1" data-testid={`${idPrefix}-style-text`}>{active?.text}</p>
                <CopyBtn text={active?.text} testid={`${idPrefix}-style-copy`} />
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      )}

      {/* Niveau de confiance */}
      {conf && (
        <div className={`rounded-2xl border p-4 ${conf.cls}`} data-testid={`${idPrefix}-confidence`}>
          <p className="text-xs uppercase tracking-[0.18em] opacity-70 mb-1">Niveau de confiance</p>
          <p className="text-sm font-medium">{conf.dot} {data.confidence.label || conf.label}</p>
        </div>
      )}

      {/* Sources utilisées */}
      {hasSources && (
        <div className="card-surface p-5" data-testid={`${idPrefix}-sources`}>
          <span className="text-gold text-xs uppercase tracking-[0.18em] flex items-center gap-2 mb-3">
            <BookOpen size={14} /> Sources utilisées
          </span>

          {principales.length > 0 && (
            <div className="mb-4" data-testid={`${idPrefix}-sources-principales`}>
              <p className="text-white/40 text-[11px] uppercase tracking-[0.15em] mb-2">Sources principales</p>
              <div className="flex flex-wrap gap-2">
                {principales.map((s, i) => (
                  <Link key={i} to={`/auteurs/${s.slug}`} data-testid={`${idPrefix}-source-author-${i}`}
                    className="group inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-white/[0.03] px-3 py-1.5 text-xs text-white/85 hover:border-gold/50 hover:text-gold transition-colors">
                    <span>{DOT[s.confidence] || ""}</span>
                    <span>{s.name}</span>
                    <ArrowUpRight size={12} className="opacity-50 group-hover:opacity-100" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {livres.length > 0 && (
            <div className="mb-4" data-testid={`${idPrefix}-sources-livres`}>
              <p className="text-white/40 text-[11px] uppercase tracking-[0.15em] mb-2">Livres recommandés</p>
              <ul className="space-y-1">
                {livres.map((b, i) => (
                  <li key={i} className="text-white/75 text-sm flex items-start gap-2">
                    <span className="text-gold/60 mt-1">•</span><span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {auteurs.length > 0 && (
            <div data-testid={`${idPrefix}-sources-auteurs`}>
              <p className="text-white/40 text-[11px] uppercase tracking-[0.15em] mb-2">Auteurs associés</p>
              <div className="flex flex-wrap gap-2">
                {auteurs.map((a, i) => (
                  <Link key={i} to={`/auteurs/${a.slug}`} data-testid={`${idPrefix}-related-author-${i}`}
                    className="inline-flex items-center gap-1 rounded-full border border-white/10 px-3 py-1 text-xs text-white/55 hover:text-gold hover:border-gold/40 transition-colors">
                    {a.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Voir les arguments opposés */}
      {opp && (opp.position || opp.objections || opp.state) && (
        <div className="card-surface p-5" data-testid={`${idPrefix}-opposing`}>
          <button onClick={() => setShowOpp((s) => !s)} data-testid={`${idPrefix}-opposing-toggle`}
            className="w-full flex items-center justify-between text-left">
            <span className="text-gold text-sm font-medium flex items-center gap-2">
              <ShieldQuestion size={16} /> Voir les arguments opposés
            </span>
            <ChevronDown size={18} className={`text-white/50 transition-transform ${showOpp ? "rotate-180" : ""}`} />
          </button>
          <AnimatePresence>
            {showOpp && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }} className="overflow-hidden">
                <div className="pt-4 space-y-3">
                  {opp.position && (
                    <div data-testid={`${idPrefix}-opposing-position`}>
                      <p className="text-white/40 text-[11px] uppercase tracking-[0.15em] mb-1">Position exposée</p>
                      <p className="text-white/80 text-sm leading-relaxed">{opp.position}</p>
                    </div>
                  )}
                  {opp.objections && (
                    <div data-testid={`${idPrefix}-opposing-objections`}>
                      <p className="text-white/40 text-[11px] uppercase tracking-[0.15em] mb-1">Objections / critiques académiques</p>
                      <p className="text-white/80 text-sm leading-relaxed">{opp.objections}</p>
                    </div>
                  )}
                  {opp.state && (
                    <div data-testid={`${idPrefix}-opposing-state`}>
                      <p className="text-white/40 text-[11px] uppercase tracking-[0.15em] mb-1">État actuel du débat</p>
                      <p className="text-white/80 text-sm leading-relaxed">{opp.state}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
